import { useState, useEffect, useCallback } from "react"
import { 
  Globe, Copy, Download, Trash2, RefreshCw, 
  CheckCircle2, XCircle, Clock, FileText, History,
  Zap, Layers, Sun, Moon, Sparkles,
  PanelLeftClose, PanelLeftOpen, ExternalLink, Wand2,
  Menu, Smartphone, Share2
} from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Toaster } from "@/components/ui/toaster"
import { toast } from "@/hooks/use-toast"
import { cn, downloadFile, getHostname, formatDate } from "@/lib/utils"
import ApiPortal from "./pages/ApiPortal"

const API_URL = import.meta.env.VITE_API_URL || "https://url2md-worker.gpts-creator.workers.dev"

interface LogEntry {
  id: string
  type: "input" | "output" | "error"
  url: string
  title: string
  icon?: string
  content: string
  timestamp: string
  status: "success" | "error"
}

interface BatchItem {
  id: number
  url: string
  status: "pending" | "converting" | "success" | "error"
  result?: LogEntry
}

function App() {
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(window.location.pathname);
    };
    
    window.addEventListener("popstate", handlePopState);
    
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Render API Portal if on that route
  if (currentRoute.startsWith("/api-portal")) {
    return <ApiPortal />;
  }

  // Main URL2MD interface continues here
  const [darkMode, setDarkMode] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<"history" | "logs">("history")
  const [activeMode, setActiveMode] = useState<"single" | "batch">("single")
  const [singleUrl, setSingleUrl] = useState("")
  const [singleLoading, setSingleLoading] = useState(false)
  const [singleResult, setSingleResult] = useState<LogEntry | null>(null)
  const [batchUrls, setBatchUrls] = useState("")
  const [batchList, setBatchList] = useState<BatchItem[]>([])
  const [batchLoading, setBatchLoading] = useState(false)
  const [logs, setLogs] = useState<LogEntry[]>([])
  const LOGS_KEY = "url2md-logs"
  const HISTORY_KEY = "url2md-history"
  const [history, setHistory] = useState<LogEntry[]>([])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedLogs = localStorage.getItem(LOGS_KEY)
      const savedHistory = localStorage.getItem(HISTORY_KEY)
      if (savedLogs) { try { setLogs(JSON.parse(savedLogs)) } catch {} }
      if (savedHistory) { try { setHistory(JSON.parse(savedHistory)) } catch {} }
    }
    setMounted(true)
  }, [])

  const saveLogs = useCallback((newLogs: LogEntry[]) => {
    setLogs(newLogs)
    localStorage.setItem(LOGS_KEY, JSON.stringify(newLogs))
  }, [])

  const saveHistory = useCallback((newHistory: LogEntry[]) => {
    setHistory(newHistory)
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
  }, [])

  const addLog = useCallback((entry: Omit<LogEntry, "id" | "timestamp">) => {
    const newEntry: LogEntry = {
      ...entry,
      id: Date.now().toString(36) + Math.random().toString(36).substring(2, 8),
      timestamp: new Date().toISOString()
    }
    setLogs(prev => {
      const updated = [newEntry, ...prev.slice(0, 99)]
      localStorage.setItem(LOGS_KEY, JSON.stringify(updated))
      return updated
    })
    return newEntry
  }, [])

  const addToHistory = useCallback((entry: LogEntry) => {
    setHistory(prev => {
      const updated = [entry, ...prev.filter(h => h.url !== entry.url).slice(0, 49)]
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const clearLogs = useCallback(() => {
    setLogs([])
    localStorage.removeItem(LOGS_KEY)
    toast({ title: "Logs Cleared", description: "All logs cleared" })
  }, [])

  const clearHistory = useCallback(() => {
    setHistory([])
    localStorage.removeItem(HISTORY_KEY)
    toast({ title: "History Cleared", description: "All history cleared" })
  }, [])

  const deleteLog = useCallback((id: string) => {
    setLogs(prev => {
      const updated = prev.filter(l => l.id !== id)
      localStorage.setItem(LOGS_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const deleteHistory = useCallback((id: string) => {
    setHistory(prev => {
      const updated = prev.filter(h => h.id !== id)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  const loadHistoryItem = useCallback((item: LogEntry) => {
    setSingleResult(item)
    setSingleUrl(item.url)
    setActiveMode("single")
    setSidePanelOpen(false)
  }, [])

  useEffect(() => {
    if (!mounted) return
    if (darkMode) { document.documentElement.classList.add("dark") } 
    else { document.documentElement.classList.remove("dark") }
  }, [darkMode, mounted])

  const handleSingleConvert = async () => {
    if (!singleUrl.trim()) {
      toast({ title: "Error", description: "Please enter a URL", variant: "destructive" })
      return
    }
    try { new URL(singleUrl) } catch {
      toast({ title: "Error", description: "Invalid URL format", variant: "destructive" })
      return
    }
    setSingleLoading(true)
    setSingleResult(null)
    const inputLog = addLog({ type: "input", url: singleUrl, title: getHostname(singleUrl), content: singleUrl, status: "success" })
    try {
      const response = await fetch(`${API_URL}/api/convert?url=${encodeURIComponent(singleUrl)}`)
      const text = await response.text()
      let data: Record<string, unknown> = {}
      try { data = JSON.parse(text) } catch {}
      const markdown = (data.markdown as string) || text
      const icon = (data.icon as string) || null
      const result: LogEntry = { id: inputLog.id, type: "output", url: singleUrl, title: (data.title as string) || getHostname(singleUrl), icon, content: markdown, timestamp: new Date().toISOString(), status: response.ok ? "success" : "error" }
      setLogs(prev => prev.map(l => l.id === inputLog.id ? { ...l, type: "output", content: markdown, icon, status: result.status } : l))
      if (response.ok) {
        addToHistory(result)
        setSingleResult(result)
        toast({ title: "Success", description: "URL converted", variant: "success" })
      } else {
        setLogs(prev => prev.map(l => l.id === inputLog.id ? { ...l, type: "error", content: (data.error as string) || "Failed", status: "error" } : l))
        toast({ title: "Error", description: (data.error as string) || "Failed", variant: "destructive" })
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed"
      setLogs(prev => prev.map(l => l.id === inputLog.id ? { ...l, type: "error", content: errorMsg, status: "error" } : l))
      toast({ title: "Error", description: errorMsg, variant: "destructive" })
    } finally { setSingleLoading(false) }
  }

  const handleBatchConvert = async () => {
    const urls = batchUrls.split("\n").map(u => u.trim()).filter(u => u)
    if (urls.length === 0) {
      toast({ title: "Error", description: "Enter at least one URL", variant: "destructive" })
      return
    }
    for (const url of urls) {
      try { new URL(url) } catch {
        toast({ title: "Error", description: `Invalid URL: ${url}`, variant: "destructive" })
        return
      }
    }
    const initialList: BatchItem[] = urls.map((url, index) => ({ id: index, url, status: "pending" }))
    setBatchList(initialList)
    setBatchLoading(true)
    for (let i = 0; i < initialList.length; i++) {
      const item = initialList[i]
      setBatchList(prev => prev.map(b => b.id === item.id ? { ...b, status: "converting" } : b))
      const inputLog = addLog({ type: "input", url: item.url, title: getHostname(item.url), content: item.url, status: "success" })
      try {
        const response = await fetch(`${API_URL}/api/convert?url=${encodeURIComponent(item.url)}`)
        const text = await response.text()
        let data: Record<string, unknown> = {}
        try { data = JSON.parse(text) } catch {}
        const markdown = (data.markdown as string) || text
        const icon = (data.icon as string) || null
        const result: LogEntry = { id: inputLog.id, type: "output", url: item.url, title: (data.title as string) || getHostname(item.url), icon, content: markdown, timestamp: new Date().toISOString(), status: response.ok ? "success" : "error" }
        setBatchList(prev => prev.map(b => b.id === item.id ? { ...b, status: response.ok ? "success" : "error", result } : b))
        setLogs(prev => prev.map(l => l.id === inputLog.id ? { ...l, type: "output", content: markdown, icon, status: result.status } : l))
        if (response.ok) { addToHistory(result) }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Failed"
        setBatchList(prev => prev.map(b => b.id === item.id ? { ...b, status: "error" } : b))
        setLogs(prev => prev.map(l => l.id === inputLog.id ? { ...l, type: "error", content: errorMsg, status: "error" } : l))
      }
    }
    setBatchLoading(false)
    toast({ title: "Complete", description: "Batch conversion finished" })
  }

  const handleDownload = (content: string, url: string) => {
    const filename = `${getHostname(url)}.md`
    downloadFile(content, filename)
    toast({ title: "Downloaded", description: `Saved as ${filename}` })
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: "Copied", description: "Copied to clipboard" })
  }

  const handleShare = async (url: string) => {
    if (navigator.share) {
      try { await navigator.share({ title: 'URL2MD', text: 'Check out this Markdown', url }) } 
      catch { navigator.clipboard.writeText(url); toast({ title: "Link Copied" }) }
    } else {
      navigator.clipboard.writeText(url)
      toast({ title: "Link Copied" })
    }
  }

  if (!mounted) return null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f] transition-colors duration-300">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-[#0a0a0f]/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 lg:hidden">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidePanelOpen(true)} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 dark:from-blue-500 dark:to-purple-600 flex items-center justify-center text-white font-bold text-sm">M</div>
              <span className="font-bold text-gray-900 dark:text-white">URL2MD</span>
            </div>
          </div>
          <button onClick={() => setDarkMode(!darkMode)} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
            {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden lg:sticky lg:top-0 lg:z-50 lg:flex lg:items-center lg:justify-between lg:px-8 lg:h-16 lg:bg-white/80 dark:bg-[#0a0a0f]/80 lg:backdrop-blur-md lg:border-b lg:border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 dark:from-blue-500 dark:to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">M</div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">URL2MD</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Convert URLs to Markdown</p>
            </div>
          </div>
        </div>
        <button onClick={() => setDarkMode(!darkMode)} className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200">
          {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-600" />}
        </button>
      </header>

      {/* AdSense Banner - Header */}
      <div className="w-full bg-gray-50 dark:bg-[#0a0a0f] border-b border-gray-200 dark:border-gray-800">
        <ins className="adsbygoogle"
             style={{ display: "block", width: "100%", height: "90px" }}
             data-ad-client="ca-pub-1319141536545865"
             data-ad-slot="2175586962"
             data-ad-format="horizontal"
             data-full-width-responsive="true" />
      </div>

      {/* Side Panel */}
      <aside className={cn("fixed inset-0 z-50 lg:fixed lg:left-0 lg:top-16 lg:bottom-0 lg:w-80 lg:z-30 lg:bg-white dark:bg-[#121218] lg:border-r lg:border-gray-200 dark:border-gray-800 transition-all duration-300 lg:transition-none", sidePanelOpen ? "translate-x-0 bg-white/95 dark:bg-[#0a0a0f]/95 backdrop-blur-xl" : "-translate-x-full lg:translate-x-0 lg:opacity-100 lg:pointer-events-none")}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="font-semibold text-gray-900 dark:text-white">History & Logs</h2>
            <button onClick={() => setSidePanelOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden">
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4 pb-0">
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <button onClick={() => setActiveTab("history")} className={cn("flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all", activeTab === "history" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>
                <History className="h-4 w-4 inline mr-1.5" />History
              </button>
              <button onClick={() => setActiveTab("logs")} className={cn("flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all", activeTab === "logs" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>
                <FileText className="h-4 w-4 inline mr-1.5" />Logs
              </button>
            </div>
          </div>
          <ScrollArea className="flex-1 p-4">
            {activeTab === "history" ? (
              history.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-500">No history yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((item) => (
                    <div key={item.id} onClick={() => loadHistoryItem(item)} className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 hover:bg-white dark:hover:bg-gray-800 hover:shadow-md transition-all cursor-pointer">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {item.icon ? (
                            <img src={item.icon} alt="" className="w-8 h-8 rounded-md object-contain bg-white p-0.5 border border-gray-100" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                          ) : (
                            <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-500">M</div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{item.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.url}</p>
                          </div>
                        </div>
                        {item.status === "success" ? <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 pl-11">{formatDate(item.timestamp)}</p>
                    </div>
                  ))}
                </div>
              )
            ) : (
              logs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-500">No logs yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.slice(0, 50).map((log) => (
                    <div key={log.id} className="space-y-1.5">
                      <span className={cn("text-xs px-2 py-0.5 rounded-full", log.type === "input" ? "bg-blue-100 text-blue-600" : log.type === "output" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600")}>{log.type.toUpperCase()}</span>
                      <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{log.title}</p>
                        <p className="text-xs text-gray-500 truncate">{log.url}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </ScrollArea>
          {(history.length > 0 || logs.length > 0) && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-800">
              <button onClick={() => activeTab === "history" ? clearHistory() : clearLogs()} className="w-full py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center justify-center gap-2">
                <Trash2 className="h-4 w-4" />Clear {activeTab === "history" ? "History" : "Logs"}
              </button>
            </div>
          )}
        </div>
      </aside>

      {sidePanelOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidePanelOpen(false)} />}

      <main className="lg:ml-80">
        <div className="container mx-auto px-4 py-6 lg:py-10 max-w-4xl">
          <div className="text-center mb-6 lg:mb-10 animate-fade-in">
            <h1 className="text-2xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">Convert Any URL to Markdown</h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Transform webpages into clean Markdown with a single click</p>
          </div>

          {/* Mode Tabs */}
          <div className="flex justify-center mb-4 lg:mb-8 animate-slide-up">
            <div className="inline-flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
              <button onClick={() => setActiveMode("single")} className={cn("px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2", activeMode === "single" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>
                <Zap className="h-4 w-4" /><span className="hidden lg:inline">Single</span>
              </button>
              <button onClick={() => setActiveMode("batch")} className={cn("px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2", activeMode === "batch" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>
                <Layers className="h-4 w-4" /><span className="hidden lg:inline">Batch</span>
              </button>
            </div>
          </div>

          {/* Input Card */}
          <div className="bg-white dark:bg-[#121218] rounded-xl lg:rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden mb-4 lg:mb-6 animate-slide-up">
            <div className="p-4 lg:p-6">
              {activeMode === "single" ? (
                <div className="flex gap-2 lg:gap-3">
                  <div className="flex-1 relative hidden lg:block">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input type="url" placeholder="Paste your URL..." className="w-full pl-12 py-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-base text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" value={singleUrl} onChange={(e) => setSingleUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSingleConvert()} />
                  </div>
                  <input type="url" placeholder="Paste URL..." className="flex-1 pl-3 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 lg:hidden" value={singleUrl} onChange={(e) => setSingleUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSingleConvert()} />
                  <button onClick={handleSingleConvert} disabled={singleLoading} className="px-4 lg:px-6 py-3 lg:py-4 bg-gray-900 dark:bg-blue-600 text-white font-medium rounded-xl hover:bg-gray-800 dark:hover:bg-blue-500 transition-all disabled:opacity-50 flex items-center gap-2">
                    {singleLoading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                    <span className="hidden lg:inline">{singleLoading ? "..." : "Convert"}</span>
                  </button>
                </div>
              ) : (
                <div>
                  <textarea className="w-full h-32 lg:h-40 p-3 lg:p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-all" placeholder="One URL per line..." value={batchUrls} onChange={(e) => setBatchUrls(e.target.value)} />
                  <button onClick={handleBatchConvert} disabled={batchLoading} className="w-full mt-3 lg:mt-4 py-3 lg:py-4 bg-gray-900 dark:bg-blue-600 text-white font-medium rounded-xl hover:bg-gray-800 dark:hover:bg-blue-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {batchLoading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
                    {batchLoading ? "Converting..." : "Start Batch"}
                  </button>
                </div>
              )}
            </div>
            {batchList.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-800">
                <div className="px-4 py-3 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2"><Clock className="h-4 w-4" />Progress</span>
                  <span className="text-sm text-gray-500">{batchList.filter(b => b.status === "success").length} / {batchList.length}</span>
                </div>
                <div className="max-h-48 lg:max-h-64 overflow-y-auto">
                  {batchList.map((item) => (
                    <div key={item.id} className={cn("flex items-center justify-between p-3 lg:p-4", item.status === "pending" && "bg-white", item.status === "converting" && "bg-blue-50 dark:bg-blue-900/10", item.status === "success" && "bg-green-50 dark:bg-green-900/10", item.status === "error" && "bg-red-50 dark:bg-red-900/10")}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {item.status === "pending" && <Clock className="h-4 w-4 text-gray-400" />}
                        {item.status === "converting" && <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />}
                        {item.status === "success" && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                        {item.status === "error" && <XCircle className="h-4 w-4 text-red-500" />}
                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">{getHostname(item.url)}</span>
                      </div>
                      {item.result && item.status === "success" && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleCopy(item.result.content)} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"><Copy className="h-4 w-4" /></button>
                          <button onClick={() => handleDownload(item.result.content, item.result.url)} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"><Download className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Result Card */}
          {singleResult && (
            <div className="bg-white dark:bg-[#121218] rounded-xl lg:rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg overflow-hidden animate-scale-in">
              <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {singleResult.icon ? (
                    <img src={singleResult.icon} alt="" className="w-8 h-8 rounded-md object-contain bg-white p-0.5 border border-gray-100" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                  ) : (
                    singleResult.status === "success" ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-sm lg:text-base text-gray-900 dark:text-white truncate">{singleResult.title}</p>
                    <a href={singleResult.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-500 flex items-center gap-1 truncate">
                      {getHostname(singleResult.url)}<ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleCopy(singleResult.content)} disabled={singleResult.status !== "success"} className="px-3 py-1.5 text-xs lg:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-1.5">
                    <Copy className="h-4 w-4" />Copy
                  </button>
                  <button onClick={() => handleDownload(singleResult.content, singleResult.url)} disabled={singleResult.status !== "success"} className="px-3 py-1.5 text-xs lg:text-sm font-medium text-white bg-gray-900 dark:bg-blue-600 rounded-lg hover:bg-gray-800 dark:hover:bg-blue-500 flex items-center gap-1.5">
                    <Download className="h-4 w-4" />Save
                  </button>
                  <button onClick={() => handleShare(singleResult.url)} className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 lg:hidden">
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <ScrollArea className="h-64 lg:h-96">
                <pre className="p-4 text-xs lg:text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{singleResult.status === "success" ? singleResult.content : singleResult.content}</pre>
              </ScrollArea>
            </div>
          )}

          {/* AdSense - Bottom Banner */}
          <div className="mt-4 px-4">
            <ins className="adsbygoogle"
                 style={{ display: "block", width: "100%", height: "50px" }}
                 data-ad-client="ca-pub-1319141536545865"
                 data-ad-slot="4252268638"
                 data-ad-format="rectangle"
                 data-full-width-responsive="true" />
          </div>

          {/* Footer */}
          <footer className="mt-8 text-center text-xs lg:text-sm text-gray-500 dark:text-gray-400">
            <p>Powered by Cloudflare Workers</p>
          </footer>
        </div>
      </main>
      <Toaster />
      <script dangerouslySetInnerHTML={{
        __html: `(adsbygoogle = window.adsbygoogle || []).push({});`
      }} />
    </div>
  )
}

export default App
